import React, { useEffect, useState } from "react";
import clsx from "clsx";
import BigNumber from "bignumber.js";
import { ArrowRight, Shield } from "react-feather";
import { PublicKey } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { useMediaQuery } from "usehooks-ts";
import { useAppSelector } from "@axvdex/state";
import useLanguage from "@axvdex/hooks/useLanguage";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import styles from "@axvdex/styles/Outbid.module.scss";
import CustomModal from "@axvdex/components/common/CustomModal";
import CustomLoader from "@axvdex/components/common/CustomLoader";
import { useGetBids } from "@axvdex/hooks/solana/useOutbid";
import { Sale } from "@axvdex/types/solana/outbid";
import { BidInfo } from "@axvdex/solana/outbidAPI/js/generated";
import { ReactComponent as IcnSword } from "@axvdex/assets/icons/icn-sword.svg";
import { noNicknamesCheck, sanitizeNickname } from "@axvdex/utils/nicknamesSanitization";
import { NicknameInfo } from "@axvdex/types/solana/nickname";
import { useUserSettings } from "@axvdex/hooks/useUserSettings";
import { responsiveBreakpoints } from "@axvdex/constants";
import SaleNicknameTargetModal from "./SolanaSaleNicknameTargetModal";

interface SaleLeaderboardModalProps {
  saleLeaderboardModalOpen: {
    open: boolean;
    outbid: IOutbidInfoSaleLeaderboardInput | null;
    selectedSale: Sale | null;
  };
  setSaleLeaderboardModalOpen: React.Dispatch<
    React.SetStateAction<{ open: boolean; outbid: IOutbidInfoSaleLeaderboardInput | null; selectedSale: Sale | null }>
  >;
  nicknames: Map<string, NicknameInfo>;
  earlySuccessSetNickname?: (targetPublicKey: PublicKey, nickname: string) => void;
}

export interface IOutbidInfoSaleLeaderboardInput {
  address: string;
  project: string;
}

interface ITableRow {
  ticketTotalAmount: string;
  address: string;
}

function SaleLeaderboardModal({
  saleLeaderboardModalOpen,
  setSaleLeaderboardModalOpen,
  nicknames,
  earlySuccessSetNickname,
}: SaleLeaderboardModalProps) {
  const { i18 } = useLanguage();
  const { userSettings } = useUserSettings();

  const isMobileSmBreakpoint = useMediaQuery(responsiveBreakpoints.mobile);
  const [tableData, setTableData] = useState<ITableRow[]>([]);
  const [inLeaderboardAddress, setInLeaderboardAddress] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const walletInfo = useAppSelector(selectWalletInfo);
  const { connection } = useConnection();

  const outbid = saleLeaderboardModalOpen.outbid;
  const selectedSale = saleLeaderboardModalOpen.selectedSale;

  const title =
    i18("Leaderboard", "sale.leaderboard") +
    (outbid ? ": " + outbid.project : "") +
    (selectedSale ? " | " + i18("Sale", "sale.leaderboard.title") + " " + selectedSale.id : "");

  useEffect(() => {
    if (saleLeaderboardModalOpen.open) getTable();
    if (!saleLeaderboardModalOpen.open) setTableData([]);
  }, [saleLeaderboardModalOpen.open]);

  const getTable = async () => {
    setLoading(true);
    // Cosmos call the API, Solana call the blockchain
    const bids = await useGetBids(connection, selectedSale);
    const data = aggregateTickerAmounts(bids, selectedSale.config.targetAllocation.targetAssetDecimals);
    setTableData(data);
    const newSet = new Set(data.map(row => row.address));
    setInLeaderboardAddress(newSet);
    setLoading(false);
  };

  return (
    <>
      <CustomModal
        extraClassName="outbidGuide"
        isOpen={saleLeaderboardModalOpen.open}
        onClose={() => setSaleLeaderboardModalOpen({ open: false, selectedSale: null, outbid: null })}
        modalSize="lg"
      >
        <section className="sectionModalHeader">
          <h2 className="h2">{title}</h2>
        </section>
        {loading ? (
          <div style={{ textAlign: "center" }}>
            <CustomLoader size="sm" />
          </div>
        ) : (
          <div
            className={clsx(styles.sales, "sales")}
            style={{ display: "flex", flexFlow: isMobileSmBreakpoint ? "column" : undefined }}
          >
            <div
              className={"custom-scrollbar"}
              style={{
                fontSize: "1em",
                width: isMobileSmBreakpoint ? "100%" : "50%",
                paddingRight: "1em",
                maxHeight: "30em",
                overflowY: "scroll",
              }}
            >
              <table className={clsx("table")}>
                <thead>
                  <tr>
                    <th style={{ width: "5em", textAlign: "center" }}>Place</th>
                    <th style={{ textAlign: "center" }}>Nickname</th>
                    <th style={{ textAlign: "center" }}>Ticket Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row: ITableRow, index: number) => (
                    <tr
                      key={row.address}
                      style={{
                        textAlign: "center",
                        fontWeight: walletInfo.walletAddress === row.address ? "bold" : undefined,
                        backgroundImage:
                          walletInfo.walletAddress === row.address
                            ? "linear-gradient(92.22deg, #0BF6CC 0.58%, #073DC8 100%)"
                            : undefined,
                        backgroundClip: walletInfo.walletAddress === row.address ? "text" : undefined,
                        WebkitBackgroundClip: walletInfo.walletAddress === row.address ? "text" : undefined,
                        color: walletInfo.walletAddress === row.address ? "transparent" : undefined,
                      }}
                    >
                      <td>{index + 1}</td>
                      <td>
                        {nicknames.get(row.address) && walletInfo.isConnected ? (
                          <div className="inlineFlexbox">
                            {nicknames.get(row.address).isProtected && (
                              <Shield style={{ color: "white", width: "1.5em" }} />
                            )}
                            {nicknames.get(row.address).hasChangedSomeoneElse && (
                              <IcnSword style={{ width: "1.5em" }} />
                            )}
                            {noNicknamesCheck(nicknames.get(row.address).nickname, userSettings)}
                            {!nicknames.get(row.address).isProtected && row.address !== walletInfo.walletAddress && (
                              <SaleNicknameTargetModal
                                currentNickname={nicknames.get(row.address)?.nickname || ""}
                                targetAddress={row.address}
                                earlySuccessSetNickname={earlySuccessSetNickname}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="inlineFlexbox">
                            {"------"}
                            {outbid && row.address !== walletInfo.walletAddress && (
                              <SaleNicknameTargetModal
                                currentNickname={nicknames.get(row.address)?.nickname || ""}
                                targetAddress={row.address}
                                earlySuccessSetNickname={earlySuccessSetNickname}
                              />
                            )}
                          </div>
                        )}
                      </td>
                      {/* <td>{parseFloat(row.bidCount).toLocaleString("en-US")}</td> */}
                      <td>{parseFloat(row.ticketTotalAmount).toLocaleString("en-US")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              className={"custom-scrollbar"}
              style={{
                fontSize: "1em",
                width: isMobileSmBreakpoint ? "100%" : "50%",
                marginTop: isMobileSmBreakpoint ? "2em" : "0",
                marginLeft: "1em",
                maxHeight: "30em",
                overflowY: "scroll",
              }}
            >
              <h2 style={{ textAlign: "center" }}>Activity</h2>
              {nicknames &&
                // TODO: Add full history when this backend feature is implemented
                // Need the "from_nickname" field to be filled in the backend
                Array.from(nicknames.entries())
                  .filter((entry: [string, NicknameInfo]) => inLeaderboardAddress.has(entry[0]))
                  .sort((a, b) => {
                    const changedAtA = +BigNumber(a[1].changedAt.toString());
                    const changedAtB = +BigNumber(b[1].changedAt.toString());
                    return changedAtB - changedAtA;
                  })
                  .map((entry: [string, NicknameInfo], i: number) => {
                    const [publicKey, nickname] = entry;
                    let text = "";
                    let icon = null;
                    let color = "rgba(255,255,255,0.1)";
                    if (publicKey === nickname.changedBy.toBase58()) {
                      text = `${
                        /*nickname.from_nickname !== "" ? sanitizeNickname(nickname.from_nickname, userSettings) :*/
                        "------"
                        } self-identifies as ${sanitizeNickname(nickname.nickname, userSettings)}`;
                      icon = <ArrowRight style={{ width: "1.5em", marginRight: "1em" }} />;
                    } else {
                      text = `${nicknames.get(nickname.changedBy.toBase58())
                          ? sanitizeNickname(nicknames.get(nickname.changedBy.toBase58()).nickname, userSettings)
                          : "------"
                        } has dubbed ${
                        /* nickname.from_nickname !== "" ? sanitizeNickname(nickname.from_nickname, userSettings) : */
                        "------"
                        } as ${sanitizeNickname(nickname.nickname, userSettings)}`;
                      icon = <IcnSword style={{ width: "1.5em", marginRight: "1em" }} />;
                      color = "#952f03";
                    }
                    if (nickname.isProtected) {
                      text = `${sanitizeNickname(nickname.nickname, userSettings)}  is Unconquerable!`;
                      icon = <Shield style={{ width: "1.5em", marginRight: "1em" }} />;
                      color = "#073DC8";
                    }

                    return (
                      <p
                        key={i}
                        //className={i === 0 ? "topBidderActivityRow" : ""}
                        style={{
                          alignItems: "center",
                          display: "flex",
                          backgroundColor: color,
                          borderRadius: "1em",
                          padding: ".75em",
                          marginTop: ".5em",
                          marginBottom: ".5em",
                          marginRight: ".5em",
                        }}
                      >
                        {icon}
                        {text}
                      </p>
                    );
                  })}
            </div>
          </div>
        )}
      </CustomModal>
    </>
  );
}

export default SaleLeaderboardModal;

// HELPER ======================================================================

function aggregateTickerAmounts(bids: BidInfo[], decimal: BigNumber): { address: string; ticketTotalAmount: string }[] {
  const data = bids
    .map((bid: BidInfo) => ({ address: bid.bidder.toBase58(), ticketAmount: Number(bid.ticketAmount.toString()) }))
    .reduce((acc, { address, ticketAmount }) => {
      acc.set(address, (acc.get(address) || 0) + ticketAmount);
      return acc;
    }, new Map());
  return Array.from(data).map(([address, ticketTotalAmount]) => ({
    address,
    ticketTotalAmount: BigNumber(ticketTotalAmount).div(Math.pow(10, decimal.toNumber())).toString(10),
  }));
}
